import React, {useCallback, useContext, useEffect} from 'react'
import {
  getDate,
  getDay,
  getDayAndMonthDate,
  getEndTime,
  getEnumPart,
  getTime,
  pickColorTheme,
} from '_helper/calendarFunctions'
import PropTypes from 'prop-types'
import RoundedButton from '_component/RoundedButton'
import {ReactComponent as ArrowLeft} from '_asset/svg/arrow_left.svg'
import {ReactComponent as Clock} from '_asset/svg/clock.svg'
import EventForm from '_component/eventDetail/EventForm'
import {ERROR_MSG, PLATFORM, PRIVACY, TIME_SLOT, ZONES} from '_helper/constants'
import {Trans} from '@lingui/macro'
import {EventsContext} from '_helper/context'
import httpClient from '../../_helper/httpClient'
import endpoints from '../../_config/endpoints'
import {fireErrorToast} from '../../_helper/functions'

import '../../App/App.scss'
import {useNavigate} from 'react-router-dom'
import {Box} from '@mui/material'
import {ReactComponent as ArrowDown} from '_asset/svg/arrow_down.svg'

const EventDetail = (props) => {
  const {closeEventDetail, mobileView, eventId, date} = props
  const {state} = useContext(EventsContext) // Access context
  const {eventData} = state
  const navigate = useNavigate()
  let {dispatch} = useContext(EventsContext)

  const [loading, setLoading] = React.useState(true)
  const [eventDetailData, setEventDetailData] = React.useState({})

  const {
    zone,
    platform,
    privacy,
    description,
    from,
    timeSlot,
    name,
    id,
    organization,
    pictureUrl,
    length,
    hideRegistration,
    externalRegistrationUrl,
  } = eventDetailData

  useEffect(() => {
    dispatch({type: 'TOGGLE_RESIZE', data: true})
    fetchEventDetail()
  }, [eventData])

  const fetchEventDetail = useCallback(() => {
    if (!eventData.eventId || !eventData.date) {
      setLoading(false)
      return
    }

    // Prevent unnecessary fetch if data is already loaded
    if (eventDetailData.id === eventData.eventId && eventDetailData.date === eventData.date) {
      setLoading(false)
      return
    }

    setLoading(true)

    const dateFormatted = getDate(eventData.date)
    httpClient
      .get(`${endpoints.events}/${eventData.eventId}/date/${dateFormatted}`)
      .then((res) => {
        setEventDetailData(res.data)
        setLoading(false)
      })
      .catch((err) => {
        fireErrorToast(ERROR_MSG)
        setLoading(false)
        console.error(err)
      })
  }, [eventData, eventDetailData])

  const isValidUrl = (url) => {
    try {
      return url && url.trim() && new URL(url)
    } catch {
      return false
    }
  }

  return (
    <div className="content-wrapper" style={{padding: 0, margin: 0, width: '100%'}}>
      <div className={`detail-cell ${pickColorTheme(eventDetailData, true)}`}>
        <div key="detail-event" className="detail-cell-content">
          {loading ? (
            <div className="empty-cell day-detail">
              <Trans>Loading...</Trans>
            </div>
          ) : (
            <>
              <div className="header-wrapper">
                <div className="button-icon">
                  <ArrowLeft onClick={() => closeEventDetail()} />
                </div>
                {mobileView ? (
                  <div className="mobile-header-event-detail">
                    <div className="header-day">
                      <span className="header-text">{from && getDay(from)}</span>
                      <span className="header-text day">{from && getDayAndMonthDate(from)}</span>
                    </div>
                    <div className="header-time">
                      <span className="header-text time">
                        <span>{from && getTime(from)}</span>
                        <span>&nbsp;&#8208;&nbsp;{from && length && getEndTime(from, length)}</span>
                      </span>
                      <span className="header-text">
                        {timeSlot && getEnumPart(TIME_SLOT, timeSlot, 'label')}
                      </span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="header-day">
                      <span className="header-text">{from && getDay(from)}</span>
                      <span className="header-text day">{from && getDayAndMonthDate(from)}</span>
                    </div>
                    <hr className="header-divider" />
                    <Clock className="time" />
                    <span className="header-text time">
                      <span>{from && getTime(from)}</span>
                      <span>&nbsp;&#8208;&nbsp;{from && length && getEndTime(from, length)}</span>
                    </span>
                    <span className="header-text">
                      {timeSlot && getEnumPart(TIME_SLOT, timeSlot, 'label')}
                    </span>
                  </>
                )}
              </div>
              <div className="event-detail-cell-wrapper">
                <div className="event-header">
                  <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                    <div className="name-org">
                      <div className="name">{name}</div>
                      <div className="organization">{organization && organization.name}</div>
                    </div>
                    <div className="additional-info">
                      <div className="button-group">
                        {platform && (
                          <div className="button">
                            <RoundedButton
                              icon={getEnumPart(PLATFORM, platform, 'icon')}
                              text={getEnumPart(PLATFORM, platform, 'label')}
                              staticBtn
                            />
                          </div>
                        )}
                        {privacy && (
                          <div className="button">
                            <RoundedButton
                              icon={getEnumPart(PRIVACY, privacy, 'icon')}
                              text={getEnumPart(PRIVACY, privacy, 'label')}
                              staticBtn
                            />
                          </div>
                        )}
                      </div>
                      <div className="zone">{zone && getEnumPart(ZONES, zone, 'label')}</div>
                    </div>
                  </div>
                  <div>
                    <div className="image event-detail">
                      {isValidUrl(pictureUrl) ? (
                        <img
                          className="picture"
                          alt="logo"
                          src={pictureUrl}
                          onError={(e) => (e.target.style.display = 'none')}
                        />
                      ) : isValidUrl(organization?.logoUrl) ? (
                        <img
                          alt="logo"
                          src={organization.logoUrl}
                          onError={(e) => (e.target.style.display = 'none')}
                        />
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="detail">
                  <div
                    className="description"
                    dangerouslySetInnerHTML={{__html: description && description}}
                  />
                </div>
                {!externalRegistrationUrl && !hideRegistration && zone !== 'PODCAST' && (
                  <EventForm id={id && id} date={from && getDate(from)} />
                )}
                {externalRegistrationUrl && !hideRegistration && (
                  <>
                    <div className="divider">
                      <div className="button-icon">
                        <ArrowDown />
                      </div>
                    </div>
                    <Box justifyContent="center" display="flex" m={4}>
                      <button
                        type="submit"
                        onClick={() => window.open(externalRegistrationUrl, '_blank')}
                      >
                        <Trans>Register here</Trans>
                      </button>
                    </Box>
                  </>
                )}
                <div className="header-wrapper">
                  <div className="button-icon">
                    <ArrowLeft onClick={() => closeEventDetail()} />
                  </div>
                  <hr className="header-divider" />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

EventDetail.propTypes = {
  closeEventDetail: PropTypes.func.isRequired,
  data: PropTypes.object,
  mobileView: PropTypes.bool,
  eventId: PropTypes.number,
  date: PropTypes.object,
}

export default EventDetail
